import { useState, useEffect } from 'react';

import { useSelector } from 'react-redux';

import { type PayloadAction, createSlice } from '@reduxjs/toolkit';
import { produce } from 'immer';
import { useNavigate, useSearchParams } from 'react-router-dom';

import type { RootState } from '#root//rootState';
import { logException } from '@/common/utils/logger';
import { useProfileSelection } from '@/dashboard/application/profileSelectionController';
import type { Fleet } from '@/fleet/domain/fleet';
import type { Group } from '@/group/domain/group';
import type { GroupConfigEnterprise } from '@/group/domain/groupConfig';
import type { Parking } from '@/parking/domain/parking';
import type { ReservationClass } from '@/subscription/domain/resaClass';


export enum UserRole {
    ParkstoryAdmin = 'parkstory_admin',
    HoldingManager = 'holding_manager',
    FacilityManager = 'facility_manager',
    LocalAdmin = 'local_admin',
    GroupAdmin = 'group_admin',
    User = 'user'
}

export interface ContextState {
    role?: UserRole;
}

export interface ContextState {
    superGroup?: Group,
    superFleet?: Fleet,
    superGroupConfig?: GroupConfigEnterprise,
    superGroupAdmin?: Group,
    currentResa?: ReservationClass,
    parking?: Parking,
    companies?: Group[],
    filteredCompanies?: Group[],
    parkings?: Parking[],
    allParkings?: Parking[],
    parkingAdmins?: Group[],
    inheritedGroups?: Group[],
    role?: UserRole,
    roles?: string[],
    parkstoryAdmins?: Group[]
}


const contextInitialization = () => {
    return {} as ContextState;
};

const contextSlice = createSlice({
    initialState: contextInitialization(),
    name: 'context',
    reducers: {
        clearContext() {
            return {};
        },
        getRole(state, action: PayloadAction<string>) {
            const isLocalAdmin = state.superGroupAdmin?.members?.some(member => member.user === action.payload);
            
            if (state.parkingAdmins?.some(grp => grp.members?.some(member => member.user === action.payload))) {
                state.role = UserRole.FacilityManager;
            } else if (isLocalAdmin) {
                state.role = UserRole.LocalAdmin;
            } else {
                state.role = UserRole.User;
            }
            return state;
        },

        // Get list of roles i have access to passing the user
        getRoles(state, action: PayloadAction<string>) {
            state.roles = [];
            const isHoldingManager = state.companies?.some(company => company.relatesTo?.some(config => config.targetModel === 'holding' && config.relation === 'ownership'));
            const isFacilityManager = state.parkingAdmins?.filter(group => group.relatesTo?.some(config => config.targetModel === 'parking' && config.relation === 'ownership')).some(parking => parking.members?.some(mem => mem.user === action.payload));

            const isLocalAdmin = state.companies?.some(
                company => company.groupConfigs?.some(
                    config => (config.groupConfig as GroupConfigEnterprise).superAdminsGroup?.members?.some(
                        member => member.user === action.payload
                    )
                )
            );
            const isParkstoryAdmin = state.parkstoryAdmins?.some(group => group.members?.some(member => member.user === action.payload));

            // <- check group admins? T.T
            const isUser = state.companies?.some(company => company.members?.some(member => member.user === action.payload));

            if (isParkstoryAdmin) {
                state.roles.push('parkstory_admin');
            }
            if (isHoldingManager) {
                state.roles.push('holding_manager');
            }
            if (isFacilityManager) {
                state.roles.push('facility_manager');
            }
            if (isLocalAdmin) {
                state.roles.push('local_admin');
            }
            if (isUser) {
                state.roles.push('user');
            }

            /*
             * if (isUser) {
             *     state.roles.push('user');
             * }
             */
            /*
             *if (!state.roles.includes(state.role as UserRole)) {
             *  state.roles.push(state.role as UserRole);
             *}
             */

            // Remove empty roles and duplicates
            state.roles = state.roles.filter(role => role !== undefined && state.roles?.indexOf(role) === state.roles?.lastIndexOf(role));

            return state;
        },
        setCompanies(state, action: PayloadAction<Group[]>) {
            state.companies = action.payload;
        },
        setInheritedGroups(state, action: PayloadAction<Group[]>) {
            state.inheritedGroups = action.payload;
        },
        setParkingAdmins(state, action: PayloadAction<Group[]>) {
            state.parkingAdmins = action.payload;
            return state;

        },

        setParkingCtx(state, action: PayloadAction<Parking>) {
            state.parking = action.payload;
        },

        setParkings(state, action: PayloadAction<Parking[]>) {
            state.allParkings = action.payload;
        },
        setParkstoryAdmins(state, action: PayloadAction<Group[]>) {
            state.parkstoryAdmins = action.payload;
            return state;

        },
        setRole(state, action: PayloadAction<{ user?: string, role?: UserRole }>) {
            return produce(state, draftState => {
                draftState.role = action.payload.role;
                

                // Filter companies based on user role and membership
                const filteredCompanies = draftState.companies?.filter(company => {
                    if (draftState.role === UserRole.LocalAdmin) {
                        return company.groupConfigs?.some(config => {
                            return (config.groupConfig as GroupConfigEnterprise)?.superAdminsGroup?.members?.some(member => {
                                return member.user === action.payload.user;
                            }
                            );
                        }
                        );
                    } else {
                        return company.members?.some(member =>
                            member.user === action.payload.user
                        );
                    }
                });
                draftState.filteredCompanies = filteredCompanies;

                /*
                 *if (draftState.role === UserRole.FacilityManager) {
                 *  const parkings = draftState.allParkings?.filter(park => {
                 *      return draftState.parkingAdmins?.some(admins => admins.relatesTo?.some(config => config.targetModel === 'parking' && config.relation === EnumRelation.OWNERSHIP && config.targetId == String(park.id)));
                 *  });
                 *  draftState.parkings = parkings;
                 *} else {
                 */
                draftState.parkings = draftState.allParkings;

                // }

            });
        },
        setSuperFleet(state, action: PayloadAction<Fleet>) {
            state.superFleet = action.payload;
        },
        setSuperGroup(state, action: PayloadAction<Group>) {
            state.superGroup = action.payload;
            state.superGroupConfig = state.superGroup.groupConfigs?.filter(
                groupConfig => groupConfig.groupConfig.__component === 'user.enterprise'
            ).pop()?.groupConfig as GroupConfigEnterprise;
            state.superGroupAdmin = state.superGroupConfig?.superAdminsGroup;
            state.superFleet = state.superGroupConfig?.superFleet;

            // state.parkings = filteredParkings;
            return state;
        },
        setSuperGroupAdmin(state, action: PayloadAction<Group>) {
            state.superGroupAdmin = action.payload;
            return state;
        },
        setSuperResaClass(state, action: PayloadAction<ReservationClass>) {
            state.currentResa = action.payload;
            return state;
        },

        // Unset the role, TODO: Check for local storage of the role
        unsetRole(state) {
            return produce(state, draftState => {
                draftState.role = undefined;
                draftState.companies = [];
                draftState.parkings = [];
                draftState.superGroup = undefined;
                draftState.parking = undefined;
                draftState.superGroupConfig = undefined; // Add this line to set superGroupConfig to undefined
            });
        },
        updateParkingField(state, action: PayloadAction<{ field: string, value: string }>) {
            const { field, value } = action.payload;

            // Check if parking exists
            if (!state.parking) {
                return state; // Return current state if parking doesn't exist
            }

            // Split field into nested parts
            const fieldParts = field.split('.');

            // If field is nested
            if (fieldParts.length > 1) {
                const topLevelField = fieldParts[0];
                const nestedFields = fieldParts.slice(1); // Remove the first part (top-level field)

                // Check if top-level field exists
                if (!state.parking[topLevelField]) {
                    return state; // Return current state if top-level field doesn't exist
                }

                // Create a new object with updated nested fields
                const updatedNestedFields = {
                    ...state.parking[topLevelField],
                    [nestedFields.join('.')]: value // Update the nested field
                };

                // Create a new parking object with the updated nested fields
                const updatedParking = {
                    ...state.parking,
                    [topLevelField]: updatedNestedFields // Update the top-level field
                };

                // Return the new state with updated parking
                state.parking = updatedParking;

                return state;
            } else {

                // If field is not nested, update it directly
                state.parking = {
                    ...state.parking,
                    [field]: value // Update the field
                };

                return state;
            }
        },
        updateSuperGroupConfigField(state, action: PayloadAction<{ field: string, value: string }>) {
            const { field, value } = action.payload;

            // Check if superGroupConfig exists
            if (!state.superGroupConfig) {
                return state; // Return current state if superGroupConfig doesn't exist
            }

            // Split field into nested parts
            const fieldParts = field.split('.');

            // If field is nested
            if (fieldParts.length > 1) {
                const topLevelField = fieldParts[0];
                const nestedFields = fieldParts.slice(1); // Remove the first part (top-level field)

                // Check if top-level field exists
                if (!state.superGroupConfig[topLevelField]) {
                    return state; // Return current state if top-level field doesn't exist
                }

                // Create a new object with updated nested fields
                const updatedNestedFields = {
                    ...state.superGroupConfig[topLevelField],
                    [nestedFields.join('.')]: value // Update the nested field
                };

                // Create a new superGroupConfig object with the updated nested fields
                const updatedSuperGroupConfig = {
                    ...state.superGroupConfig,
                    [topLevelField]: updatedNestedFields // Update the top-level field
                };

                // Return the new state with updated superGroupConfig
                state.superGroupConfig = updatedSuperGroupConfig;

                // Update the superGroup object with the new superGroupConfig
                state.superGroup = {
                    ...state.superGroup,
                    groupConfigs: state.superGroup?.groupConfigs?.map(groupConfig => {
                        const grpConf = {
                            ...groupConfig
                        };
                        if (groupConfig.groupConfig.__component === 'user.enterprise') {
                            grpConf.groupConfig = updatedSuperGroupConfig;
                        }
                        return grpConf;

                    }),
                };

                return state;
            } else {

                // If field is not nested, update it directly
                state.superGroupConfig = {
                    ...state.superGroupConfig,
                    [field]: value // Update the field
                };

                state.superGroup = {
                    ...state.superGroup,
                    groupConfig: {
                        ...state.superGroup?.groupConfig,
                        groupConfig: state.superGroup?.groupConfig?.groupConfig?.map(groupConfig => {
                            if (groupConfig.__component === 'user.enterprise') {
                                return state.superGroupConfig as GroupConfigEnterprise;
                            }
                            return groupConfig;
                        }) ?? []
                    },
                };

                return state;
            }
        }
    }
});

export const { setInheritedGroups, setSuperResaClass, setParkingAdmins, setParkstoryAdmins, getRole, getRoles, setSuperGroup, setSuperGroupAdmin, setSuperFleet, setCompanies, setRole, setParkingCtx, setParkings, clearContext, updateSuperGroupConfigField, unsetRole, updateParkingField } = contextSlice.actions;
export default contextSlice.reducer;

// Define AppSelector type
export type AppSelector<T> = (state: RootState) => T;

// Add access control functions
// Feature flags for the current subscription/reservation-class context. Consumers evaluate them
// with the helpers in `featureGating.ts` (default-aware), e.g. `isFeatureEnabled(features, key)`.
export const selectContextFeatures = (state: RootState) => state.context.currentResa?.config?.features;

// Subscription-level admin options for the current context. `enableWebAccess` in particular decides
// whether END USERS may manage their access profiles from the web app (i.e. this app).
export const selectContextAdminOptions = (state: RootState) => state.context.currentResa?.config?.adminOptions;

export const hasAccessToFeature = (requiredRole?: UserRole | UserRole[]): AppSelector<boolean> => (state) => {
    if (!requiredRole) {
        return false;
    }

    // For other roles, check as before
    const roles = Object.values(UserRole);

    let requiredIndexes = [];
    if (Array.isArray(requiredRole)) {
        requiredIndexes = requiredRole.map(roleItem => roles.indexOf(roleItem));

    } else {
        requiredIndexes = [roles.indexOf(requiredRole)];
    }
    const currentIndex = roles.indexOf(state.context.role);

    if (requiredIndexes.length === 0) {
        return false;
    }

    return Boolean(requiredIndexes.filter(ind => ind === currentIndex).length);
};

/*
 * Single source of truth for "can this user actually save?" — the three-dots labels
 * (Edit vs See subscriber/pricing) and the screens' own edit controls must read the same
 * selector, otherwise a role like LocalAdmin gets offered "Edit" and lands on a read-only screen.
 */
export const canEditSubscriber = hasAccessToFeature([UserRole.FacilityManager, UserRole.ParkstoryAdmin, UserRole.HoldingManager]);
export const canEditPricing = hasAccessToFeature(UserRole.FacilityManager);

export const useContext = () => {

    const { setSelectedCompany, setSelectedParking, getCompanies, getParkings, setRoleAction } = useProfileSelection();

    const user = useSelector((state: RootState) => state.auth.user);
    const park = useSelector((state: RootState) => state.context.parking);
    const cpny = useSelector((state: RootState) => state.context.superGroup);
    const companies = useSelector((state: RootState) => state.context.companies);
    const parkings = useSelector((state: RootState) => state.context.parkings);

    const [savedState, setSavedState] = useState(localStorage.getItem('pksCtx'));

    const hasContextCalc = (role: UserRole) => {
        if (role === UserRole.ParkstoryAdmin) {
            return true;
        } else if (role === UserRole.FacilityManager && park) {
            return true;
        } else if ((role === UserRole.LocalAdmin || role === UserRole.User) && park && cpny) {
            return true;
        }
        return false;
    };

    const navigate = useNavigate();


    // Saving state
    const ctx = useSelector((state: RootState) => state.context);
    const [isWaitingForLoad, setIsWaitingForLoad] = useState<boolean>(true);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const [params] = useSearchParams();

    const loadContext = async (role: UserRole) => {
        if (isLoading) return;// hasContextCalc(role);

        setIsLoading(true);
        await getParkings();
        await getCompanies();
       
        // setRoleAction(user, role);

        setIsLoading(false);// hasContextCalc(role);
    };

    useEffect(() => {
        if (user) {
            const stateSaved: { superGroup: string, parking: string, role: string } = JSON.parse(savedState ?? '{}');

            if (isWaitingForLoad) {
                try {
                    loadContext(stateSaved.role as UserRole);
                    setIsWaitingForLoad(false);
                } catch (e) {
                    logException(e);
                }
            }
        }
    }, [user, savedState]);

    /*
     * useEffect(() => {
     *
     *   // const savedState = localStorage.getItem('pksCtx');
     *
     *   if (!ctx.role) {
     *
     *       if (isWaitingForLoad) {
     *           try {
     *               //const stateSaved: { superGroup: string, parking: string, role: string } = JSON.parse(savedState ?? '{}');
     *               //loadContext(stateSaved.role as UserRole);
     *
     *               //setIsWaitingForLoad(false);
     *           } catch (e) {
     *           }
     *       } else {
     *           if (params.get('reload') === null && !isLoading) {
     *
     *               navigate('/select-profile?reload=false');
     *           }
     *       }
     *   } else if (hasContextCalc(ctx.role)) {
     *       localStorage.setItem('pksCtx', serializeContext(ctx));
     *       setSavedState(localStorage.getItem('pksCtx'));
     *
     *       // if (hasContextCalc(ctx.role) && isWaitingForCloseModal && ctx.role) setShowModal(false);
     *   }
     *
     *
     * }, [ctx, isWaitingForLoad, isLoading]);
     */


    useEffect(() => { 
        const stateSaved: { superGroup: string, parking: string, role: string } = JSON.parse(savedState ?? '{}');
        
        if (!ctx.role && stateSaved.role && hasContextCalc(stateSaved.role as UserRole)) {
            setRoleAction(user, stateSaved.role as UserRole);
        } else if (!stateSaved.role && !ctx.role) {
            if (params.get('reload') === null && !isLoading) {
                navigate('/select-profile?reload=false');
            }
        } else if (!ctx.role && stateSaved.role && !hasContextCalc(stateSaved.role as UserRole)) {
            if (params.get('reload') === null && !isLoading) {
                navigate('/select-profile?reload=false');
            }
        }
    }, [companies, parkings, park, cpny, savedState]);


    useEffect(() => { 
        const stateSaved: { superGroup: string, parking: string, role: string } = JSON.parse(savedState ?? '{}');
        
        const cpny2 = companies?.find(item => String(item.id) == stateSaved.superGroup);
       
        if (!cpny && cpny2) {
            setSelectedCompany(cpny2);
        }
    }, [companies, savedState]);
    

    useEffect(() => { 
        const stateSaved: { superGroup: string, parking: string, role: string } = JSON.parse(savedState ?? '{}');
        if (!Array.isArray(parkings)) return;
        const park2 = parkings?.find(item => String(item.id) == stateSaved.parking);
       
        if (!park && park2) {
            setSelectedParking(park2);
        }
    }, [parkings, savedState]);


    /*
     *if (cpny) setSuperGroup(cpny);
     *const prkng = parkings?.find(item => item.id?.toString() == parkingId);
     *if (prkng) setSelectedParking(prkng);
     */

    return {
        hasContextCalc() {
            return ctx.role ? hasContextCalc(ctx.role) : false;
        },
        setIsWaitingForLoad(waiting: boolean) {
            setIsWaitingForLoad(waiting);
        }
    };


};
